/**
 * Pure display logic for scenario runs and steps: labels, progress, list order and filters,
 * and the rendered question/answer rows both the trainee and the reviewer see.
 */

import type {
  QuizAnswer,
  ScenarioRun,
  ScenarioRunStatus,
  ScenarioRunStep,
  ScenarioRunStepSummary,
  ScenarioStepStatus,
} from '../types';
import { renderTemplate } from './template';
import { renderAnswer } from './scenarioSteps';
import { toMillis, type TimestampLike } from './assignmentStatus';

export const RUN_STATUS_LABELS: Record<ScenarioRunStatus, string> = {
  assigned: 'Not started',
  in_progress: 'In progress',
  completed: 'Needs review',
  verified: 'Verified',
  cancelled: 'Cancelled',
};

export const STEP_STATUS_LABELS: Record<ScenarioStepStatus, string> = {
  locked: 'Waiting',
  open: 'Open',
  answered: 'Waiting for review',
  verified: 'Verified',
  redo: 'Redo requested',
  cancelled: 'Cancelled',
};

/**
 * The same, as the step's own person sees it. Their answered step isn't waiting on anyone:
 * the next step already has it, and the reviewer only looks once every step is in.
 */
export const ASSIGNEE_STEP_STATUS_LABELS: Record<ScenarioStepStatus, string> = {
  ...STEP_STATUS_LABELS,
  answered: 'Submitted',
};

/** Someone else's step, as the other people in the run see it on the step page. */
export const PARTICIPANT_STEP_STATUS_LABELS: Record<ScenarioStepStatus, string> = {
  ...ASSIGNEE_STEP_STATUS_LABELS,
  locked: 'Not started',
  open: 'In progress',
};

/** A step its assignee can answer now. */
export function isOpenStep(step: Pick<ScenarioRunStep, 'status'>): boolean {
  return step.status === 'open' || step.status === 'redo';
}

export function countOpenSteps(steps: Pick<ScenarioRunStep, 'status'>[]): number {
  return steps.filter(isOpenStep).length;
}

export interface RunProgress {
  total: number;
  /** answered or verified */
  answered: number;
  verified: number;
}

export function runProgress(run: Pick<ScenarioRun, 'steps'>): RunProgress {
  const answered = run.steps.filter((s) => s.status === 'answered' || s.status === 'verified').length;
  const verified = run.steps.filter((s) => s.status === 'verified').length;
  return { total: run.steps.length, answered, verified };
}

/** "2 of 5 answered · 1 verified" */
export function formatRunProgress(run: Pick<ScenarioRun, 'steps'>): string {
  const p = runProgress(run);
  return `${p.answered} of ${p.total} answered · ${p.verified} verified`;
}

/** Any step answered against an earlier answer that later changed. */
export function runHasFlag(run: Pick<ScenarioRun, 'steps'>): boolean {
  return run.steps.some((s) => s.contextStale);
}

/** Assignee names in step order, each once. */
export function runPeople(run: Pick<ScenarioRun, 'steps'>): string[] {
  const out: string[] = [];
  for (const s of run.steps) if (s.assigneeName && !out.includes(s.assigneeName)) out.push(s.assigneeName);
  return out;
}

export type RunFilter = 'active' | 'done' | 'all';

export const RUN_FILTERS: { id: RunFilter; label: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'done', label: 'Done' },
  { id: 'all', label: 'All' },
];

export function filterRuns<T extends Pick<ScenarioRun, 'status'>>(runs: T[], filter: RunFilter): T[] {
  if (filter === 'all') return runs;
  const done = (r: T) => r.status === 'verified' || r.status === 'cancelled';
  return runs.filter((r) => (filter === 'done' ? done(r) : !done(r)));
}

/** "Needs review" first, then most recently updated. */
export function sortRuns<T extends Pick<ScenarioRun, 'status' | 'updatedAt' | 'createdAt'>>(runs: T[]): T[] {
  const key = (r: T) => toMillis(r.updatedAt) ?? toMillis(r.createdAt) ?? 0;
  return [...runs].sort((a, b) => {
    const an = a.status === 'completed' ? 0 : 1;
    const bn = b.status === 'completed' ? 0 : 1;
    return an !== bn ? an - bn : key(b) - key(a);
  });
}

/** Statuses the All stores roll-up counts; cancelled runs are left out. */
export type RollupStatus = Exclude<ScenarioRunStatus, 'cancelled'>;
export const ROLLUP_STATUSES: RollupStatus[] = ['assigned', 'in_progress', 'completed', 'verified'];

export interface RollupCounts {
  counts: Record<RollupStatus, number>;
  total: number;
}

export interface ScenarioRollup {
  /** One row per store with runs that count, in store order */
  rows: Array<RollupCounts & { locationId: string }>;
  /** The All stores row */
  all: RollupCounts;
}

const emptyCounts = (): RollupCounts => ({ counts: { assigned: 0, in_progress: 0, completed: 0, verified: 0 }, total: 0 });

/**
 * Quiz managers' roll-up above the All stores run list: runs per store by status. Cancelled
 * runs aren't counted, so a store whose runs were all cancelled has no row. `quizId` narrows
 * it to one template (the same filter the list uses).
 */
export function summarizeScenariosByStore(
  runs: Pick<ScenarioRun, 'locationId' | 'quizId' | 'status'>[],
  quizId: string | null = null
): ScenarioRollup {
  const byStore = new Map<string, RollupCounts>();
  const all = emptyCounts();
  for (const run of runs) {
    if (run.status === 'cancelled' || (quizId && run.quizId !== quizId)) continue;
    const row = byStore.get(run.locationId) ?? emptyCounts();
    byStore.set(run.locationId, row);
    for (const c of [row, all]) {
      c.counts[run.status] += 1;
      c.total += 1;
    }
  }
  const rows = [...byStore.entries()]
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    .map(([locationId, c]) => ({ locationId, ...c }));
  return { rows, all };
}

/** The templates these runs came from, by title: the roll-up's scenario filter. */
export function runTemplates(runs: Pick<ScenarioRun, 'quizId' | 'quizTitle'>[]): { id: string; title: string }[] {
  const seen = new Map<string, string>();
  for (const r of runs) if (r.quizId && !seen.has(r.quizId)) seen.set(r.quizId, r.quizTitle || 'Untitled scenario');
  return [...seen.entries()]
    .map(([id, title]) => ({ id, title }))
    .sort((a, b) => a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
}

/** Inbox order: redo, then open, then answered, verified, cancelled; newest first within each. */
export function sortInboxSteps<T extends Pick<ScenarioRunStep, 'status' | 'openedAt'>>(steps: T[]): T[] {
  const rank: Record<ScenarioStepStatus, number> = { redo: 0, open: 1, answered: 2, verified: 3, locked: 4, cancelled: 5 };
  return [...steps].sort(
    (a, b) => rank[a.status] - rank[b.status] || (toMillis(b.openedAt) ?? 0) - (toMillis(a.openedAt) ?? 0)
  );
}

/** Values a step's placeholders are filled with: run variables plus the answers copied in. */
export function stepValues(step: Pick<ScenarioRunStep, 'variables' | 'context'>): Record<string, string> {
  const out: Record<string, string> = { ...step.variables };
  for (const [key, entry] of Object.entries(step.context)) out[key] = entry.value;
  return out;
}

export interface AnswerRow {
  questionId: string;
  prompt: string;
  answer: string;
}

/** Each question's rendered prompt with its rendered answer ('' when unanswered). */
export function answerRows(
  step: Pick<ScenarioRunStep, 'questions' | 'variables' | 'context'>,
  answers: Record<string, QuizAnswer> | null
): AnswerRow[] {
  const values = stepValues(step);
  return step.questions.map((q) => ({
    questionId: q.id,
    prompt: renderTemplate(q.prompt, values),
    answer: q.type === 'text' ? (typeof answers?.[q.id] === 'string' ? (answers[q.id] as string) : '') : renderAnswer(q, answers?.[q.id], values),
  }));
}

/** Status line for one step on the reviewer's view. */
export function stepStatusLine(
  step: Pick<ScenarioRunStep, 'status' | 'review' | 'answeredAt' | 'openedAt' | 'lastNotice' | 'index'>,
  summaries: ScenarioRunStepSummary[]
): string {
  switch (step.status) {
    case 'verified':
      return `Verified by ${step.review?.byName || 'a reviewer'} · ${formatDateTime(step.review?.at)}`;
    case 'answered':
      return `Answered ${formatDateTime(step.answeredAt)}`;
    case 'redo':
      return `Redo requested by ${step.review?.byName || 'a reviewer'} · ${formatDateTime(step.review?.at)}`;
    case 'open': {
      const notice = step.lastNotice ? (step.lastNotice.pushed ? ' · push delivered' : ' · in-app only') : '';
      return `Open since ${formatDateTime(step.openedAt)}${notice}`;
    }
    case 'locked': {
      const blocking = summaries.find((s) => s.index < step.index && s.status !== 'answered' && s.status !== 'verified');
      return blocking ? `Waiting for step ${blocking.index + 1}` : 'Waiting';
    }
    default:
      return 'Cancelled';
  }
}

export function formatDate(t: TimestampLike): string {
  const ms = toMillis(t);
  return ms === null ? '' : new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatDateTime(t: TimestampLike): string {
  const ms = toMillis(t);
  if (ms === null) return '';
  const d = new Date(ms);
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

/**
 * Verify / Redo (and later reassign): quiz managers (admins included) at every store, and
 * Editors on Quizzes at the run's store. Mirrors quiz_scenario_logic.can_review.
 */
export function canReviewRun(isQuizManager: boolean, isStoreEditorAtRunStore: boolean): boolean {
  return isQuizManager || isStoreEditorAtRunStore;
}
